import type { IObserverFillOptions } from '@/utils/observerFill/observerFill.interface';

/**
 * Observes the DOM for changes and updates a specified field of an element
 * when the element matching the given CSS selector is added to the DOM.
 *
 * @template T - The type of the HTMLElement to observe.
 * @template K - The key of the field to update on the HTMLElement.
 *
 * @param {string} selector - The CSS selector to identify the target element.
 * @param {K} field - The field of the target element to update.
 * @param {T[K]} value - The value to set on the specified field of the target element.
 * @param {number} [timeout=-1] - The maximum time (in milliseconds) to wait for the element to appear.
 *                                If negative, no timeout is applied.
 *
 * @returns {Promise<void>} A Promise that resolves when the field is updated, or rejects if the timeout is reached.
 *
 * @example
 * // Example usage:
 * observerFill<HTMLInputElement, 'value'>('#myInput', 'value', 'Hello, World!', 5000)
 *     .then(() => console.log('Field updated successfully'))
 *     .catch((error) => console.error('Failed to update field:', error));
 */
export default function observerFill<T extends HTMLElement, K extends keyof T = keyof T>({
    field,
    selector,
    timeout = -1,
    value,
}: IObserverFillOptions<T, K>): Promise<void> {
    return new Promise((resolve, reject) => {
        // Check if element already exists
        const existingElement = document.querySelector<T>(selector);
        if (existingElement) {
            existingElement[field] = value;
            resolve();
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout>;
        let observer: MutationObserver;

        // Create timeout handler
        const handleTimeout = () => {
            observer?.disconnect();
            reject(new Error('Timeout reached while waiting for element'));
        };

        // Create mutation observer
        observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach((element) => {
                        if (!(element instanceof HTMLElement)) {
                            return;
                        }

                        if (element.matches(selector)) {
                            (element as T)[field] = value;
                            clearTimeout(timeoutId);
                            observer.disconnect();
                            resolve();
                            return;
                        }
                    });
                }
            }
        });

        // Start observing DOM changes
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        // Set timeout
        if (timeout > 0) {
            timeoutId = setTimeout(handleTimeout, timeout);
        }
    });
}
